/**
 * Day 3 after the freeze: the immutable competitor-set versions, and the
 * revision of the frozen set that is still possible before collection.
 */

import type { ReactNode } from 'react';

import {
  newIdempotencyKey,
  replaceAnalysisCompetitor,
  type AnalysisDay3State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, TextAreaField } from '../../ui.js';
import { card, selectStyle } from './run-page-styles.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { CompetitorForms } from './useCompetitorForms.js';

export function CompetitorSetHistory({
  id,
  run,
  day3,
  busy,
  mutate,
  forms,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day3: AnalysisDay3State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly forms: CompetitorForms;
}): ReactNode {
  const {
    competitorReason,
    setCompetitorReason,
    replacePrior,
    setReplacePrior,
    replaceWith,
    setReplaceWith,
  } = forms;

  return (
    <>
      {day3.setVersions.length > 0 ? (
        <div style={{ ...card, marginTop: 12 }}>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>
            Immutable competitor-set history
          </h3>
          {day3.setVersions.map((version) => (
            <p key={version.id}>
              Version {version.setVersion} · {version.contentHash.slice(0, 12)}… ·{' '}
              {version.createdAt}
            </p>
          ))}
        </div>
      ) : null}

      {run.currentStage === 'TARGET_AND_COMPETITOR_CONTENT_COLLECTION' &&
      run.executionState === 'PENDING' &&
      day3.setVersions.length > 0 ? (
        <div style={{ ...card, marginTop: 12 }}>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>Revise the frozen set</h3>
          <p style={{ color: 'var(--text-muted)' }}>
            A replacement creates a new immutable set version; prior versions remain
            unchanged.
          </p>
          <TextAreaField
            label="Revision reason"
            value={competitorReason}
            onChange={setCompetitorReason}
            rows={2}
            maxLength={4000}
          />
          <div className="mj-form-two-column">
            <label>
              <span>Replace</span>
              <select
                value={replacePrior}
                onChange={(event) => {
                  setReplacePrior(event.target.value);
                }}
                style={selectStyle}
              >
                <option value="">Choose confirmed competitor</option>
                {day3.candidates
                  .filter((candidate) => candidate.state === 'CONFIRMED')
                  .map((candidate) => (
                    <option key={candidate.businessId} value={candidate.businessId}>
                      {candidate.displayName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              <span>With</span>
              <select
                value={replaceWith}
                onChange={(event) => {
                  setReplaceWith(event.target.value);
                }}
                style={selectStyle}
              >
                <option value="">Choose qualified alternative</option>
                {day3.candidates
                  .filter(
                    (candidate) =>
                      candidate.state !== 'CONFIRMED' &&
                      candidate.qualificationState !== 'REJECTED',
                  )
                  .map((candidate) => (
                    <option key={candidate.businessId} value={candidate.businessId}>
                      {candidate.displayName}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          <Button
            variant="outline"
            disabled={!replacePrior || !replaceWith || !competitorReason.trim()}
            busy={busy === 'replace-competitor'}
            onClick={() =>
              void mutate('replace-competitor', () =>
                replaceAnalysisCompetitor(id, newIdempotencyKey(), {
                  priorCompetitorId: replacePrior,
                  replacementCompetitorId: replaceWith,
                  reason: competitorReason,
                }),
              )
            }
          >
            Replace and create new set version
          </Button>
        </div>
      ) : null}
    </>
  );
}
